import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { consultationsService } from '../services/api';
import './css/AttachmentPreview.css';

const AttachmentPreview = () => {
  const { consultationId, attachmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentInfo, setAttachmentInfo] = useState(null);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;

    const loadAttachment = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get the attachment URL with authentication
        const url = consultationsService.getAttachmentUrl(consultationId, attachmentId);
        
        // Fetch with authentication to get blob
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load attachment');
        }

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'attachment';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match) {
            filename = match[1];
          }
        }

        const contentType = response.headers.get('Content-Type');
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        setAttachmentUrl(blobUrl);
        setAttachmentInfo({
          filename,
          contentType,
          size: blob.size
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading attachment:', err);
          setError('Nie udało się załadować załącznika');
          setLoading(false);
        }
      }
    };

    loadAttachment();

    // Cleanup blob URL on unmount
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [consultationId, attachmentId]);

  const handleDownload = () => {
    if (attachmentUrl && attachmentInfo) {
      const link = document.createElement('a');
      link.href = attachmentUrl;
      link.download = attachmentInfo.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    window.close();
    // If window.close() doesn't work (if not opened by script), navigate back
    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  if (loading) {
    return (
      <div className="attachment-preview-page">
        <div className="preview-loading">
          <div className="spinner"></div>
          <p>Ładowanie załącznika...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="attachment-preview-page">
        <div className="preview-error">
          <div className="error-icon">⚠️</div>
          <h2>Błąd</h2>
          <p>{error}</p>
          <button className="close-btn" onClick={handleClose}>
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  const isImage = attachmentInfo?.contentType?.startsWith('image/');
  const isPdf = attachmentInfo?.contentType === 'application/pdf';

  return (
    <div className="attachment-preview-page">
      <div className="preview-header">
        <div className="preview-info">
          <h2>{attachmentInfo?.filename}</h2>
          <span className="preview-size">
            {(attachmentInfo?.size / 1024).toFixed(1)} KB
          </span>
        </div>
        <div className="preview-actions">
          <button className="download-btn" onClick={handleDownload}>
            ⬇ Pobierz
          </button>
          <button className="close-btn" onClick={handleClose}>
            ✕ Zamknij
          </button>
        </div>
      </div>

      <div className="preview-content">
        {isImage && (
          <div className="image-preview">
            <img src={attachmentUrl} alt={attachmentInfo?.filename} />
          </div>
        )}
        {isPdf && (
          <div className="pdf-preview">
            <iframe
              src={attachmentUrl}
              title={attachmentInfo?.filename}
              width="100%"
              height="100%"
            />
          </div>
        )}
        {!isImage && !isPdf && (
          <div className="unsupported-preview">
            <div className="unsupported-icon">📎</div>
            <h3>Podgląd niedostępny</h3>
            <p>Ten typ pliku nie może być wyświetlony w przeglądarce.</p>
            <p>Użyj przycisku "Pobierz" powyżej, aby pobrać plik.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentPreview;
